import { FormDataConsumer, regex, required, useTranslate } from 'react-admin';
import React from 'react';
import { Box, Typography } from '@material-ui/core';
import UrlInput from '../../../../base/components/customized/common-input/UrlInput';
import { BooleanInput, TextInput } from '../../../../base/components/ra/inputs';
import LanguagesSelect from './LanguagesSelect';
import DefaultLanguageSelect from './DefaultLanguageSelect';
import GroupInput from './GroupInput';
import FeaturesInput from './FeaturesInput';
import { useAuthUser, useQueryParams } from '../../../../base/hooks';
import AutocompleteFreeSpinsInput from './AutocompleteFreeSpinsInput';
import RegulationInput from './RegulationInput';
import PlayerLimitInput from './PlayerLimitInput';
import { checkCheatModeEnv } from '../../../../services/util';
import PlaceBetPayoutConfig from './PlaceBetPayoutConfig';
import ImplementationWalletInput from './ImplementationWalletInput';
import GetPlayerTokenUrlInput from './GetPlayerTokenUrlInput';
import TransferBonusUrlInput from './TransferBonusUrlInput';
import { WalletImplementationEnum } from '../../../../constant';
import CurrenciesConfig from './CurrenciesConfig';
import DefaultRoyaltyRateInput from './DefaultRoyaltyRateInput';
import { useHasReadCurrencyPermission, useHasReadInvoicePermission } from '../../../group/components/utils';

const BrandForm = (props) => {
  const t = useTranslate();
  const { groupId } = useQueryParams();
  const user = useAuthUser();
  const isCheatModeEnv = checkCheatModeEnv();
  const hasReadInvoicePermission = useHasReadInvoicePermission();
  const hasReadCurrencyPermission = useHasReadCurrencyPermission();

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        gap: 12,
      }}
    >
      <Box display="flex" flexDirection="column">
        <Typography variant="h4" gutterBottom>
          {t('common.text.generalInfo')}
        </Typography>
        <TextInput
          source="name"
          label="resources.brand.fields.name"
          validate={[
            required(t('ra.validation.required')),
            regex(/^(?!\s*$)[a-zA-Z0-9\s]{1,55}$/, t('resources.brand.validation.name')),
          ]}
        />
        <UrlInput
          source="domain"
          label="resources.brand.fields.domain"
          helperText
          validate={required(t('ra.validation.required'))}
        />
        <TextInput source="desc" multiline rows={5} label="resources.brand.fields.desc" />
      </Box>

      <Box display="flex" flexDirection="column">
        <Typography variant="h4" gutterBottom>
          {t('common.text.localization')}
        </Typography>
        <LanguagesSelect />
        <DefaultLanguageSelect />
      </Box>

      <Box display="flex" flexDirection="column">
        <Typography variant="h4" gutterBottom>
          {t('common.text.configuration')}
        </Typography>
        <GroupInput />
        {hasReadInvoicePermission && <DefaultRoyaltyRateInput />}
        {hasReadCurrencyPermission && <CurrenciesConfig />}
        <ImplementationWalletInput />
        <UrlInput
          source="authorizeUrl"
          label="resources.brand.fields.authorizeUrl"
          helperText
          validate={required(t('ra.validation.required'))}
        />
        <FormDataConsumer>
          {({ formData }) => {
            if (formData.implementation !== WalletImplementationEnum.SEAMLESS) return null;

            return (
              <>
                <UrlInput
                  source="payOutUrl"
                  label="resources.brand.fields.payOutUrl"
                  helperText
                  validate={required(t('ra.validation.required'))}
                />
                <UrlInput
                  source="placeBetUrl"
                  label="resources.brand.fields.placeBetUrl"
                  helperText
                  validate={required(t('ra.validation.required'))}
                />
                <UrlInput source="betStatusUrl" label="resources.brand.fields.betStatusUrl" helperText />
                <UrlInput source="cancelBetUrl" label="resources.brand.fields.cancelBetUrl" helperText />
                {isCheatModeEnv && (
                  <UrlInput source="createPlayerUrl" label="resources.brand.fields.createPlayerUrl" helperText />
                )}
                {isCheatModeEnv && (
                  <UrlInput source="editBalanceUrl" label="resources.brand.fields.editBalanceUrl" helperText />
                )}
              </>
            );
          }}
        </FormDataConsumer>
        <UrlInput source="getBalanceUrl" label="resources.brand.fields.getBalanceUrl" helperText />
        <GetPlayerTokenUrlInput />
        <TransferBonusUrlInput />
        <FeaturesInput label="resources.feature-flag.name" initialGroupId={groupId} />
        <RegulationInput />
        {!user.group && <AutocompleteFreeSpinsInput />}
        {isCheatModeEnv && <PlayerLimitInput />}
        <PlaceBetPayoutConfig {...props} />

        <BooleanInput label="resources.brand.fields.homeButtonEnabled" source="homeButtonEnabled" helperText={false} />
        <BooleanInput label="resources.brand.fields.enabled" source="enabled" helperText={false} />
      </Box>
    </Box>
  );
};

export default BrandForm;
